import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { FlowSensorType } from '@/types/hydraulic'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC } from 'react'
import classes from './style.module.scss'

interface FlowSensorTableProps {
  rows: FlowSensorType[]
  onEditItem: (id: number) => void
  onDeleteItem?: (id: string, interfaceId: string) => void
}

const FlowSensorTable: FC<FlowSensorTableProps> = ({
  rows,
  onEditItem,
  onDeleteItem,
}) => {
  const { interfaceId } = useAuthContext()
  const { convertLabelUnit, convertFromMetric, convertFromMetricV2 } =
    useUnits()
  const { isViewOnly } = usePermission()
  const { t } = useTranslation()
  const handleModalDelete = (id: string, interfaceId: string) => {
    if (isViewOnly) return
    onDeleteItem && onDeleteItem(id, interfaceId)
  }
  const hasTwiCustomName = rows.some((row) => !!row.twiCustomName)

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell sx={{ width: '5%' }} className={classes.borderRight}>
                #
              </TableCell>
              <TableCell
                sx={{
                  width: '7%',
                }}
                className={classes.borderRight}
              >
                {t('commonLabelsStatus')}
              </TableCell>
              <TableCell
                sx={{
                  width: '34%',
                }}
                className={classes.borderRight}
              >
                IO
              </TableCell>
              {hasTwiCustomName && (
                <TableCell
                  sx={{
                    width: '34%',
                  }}
                  className={classes.borderRight}
                >
                  Controller Name
                </TableCell>
              )}
              <TableCell
                sx={{
                  width: '424px',
                }}
                className={classes.borderRight}
              >
                {t('actualFlow')} ({convertLabelUnit('l/m')})
              </TableCell>
              <TableCell
                sx={{
                  width: '424px',
                }}
              >
                {t('commonTableHeaderAction')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              backgroundColor: 'common.white',
            }}
          >
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  component="th"
                  scope="row"
                  className={classes.borderRight}
                >
                  {row.id}
                </TableCell>

                <TableCell className={classes.borderRight}>
                  {row.active ? (
                    <Stack className={classes.actived}>
                      {t('commonStatusActive')}
                    </Stack>
                  ) : (
                    <Stack className={classes.inActived}>
                      {t('commonStatusInactive')}
                    </Stack>
                  )}
                </TableCell>

                <TableCell className={classes.borderRight}>
                  <Box color="text.main" className={classes.addressIO}>
                    {(row.io === '' || row.io === null) && 'NOT FOUND'}
                    {row.io}
                    {row.io && row.address && (
                      <>
                        <br /> <br />
                        {row.address} - {row.index}
                      </>
                    )}
                  </Box>
                </TableCell>

                {hasTwiCustomName && (
                  <TableCell className={classes.borderRight}>
                    {row.twiCustomName || '-'}
                  </TableCell>
                )}

                <TableCell className={classes.borderRight}>
                  {convertFromMetricV2(row.measuredFlow, 'l/m', 'gpm', 2)}
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing="10px">
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'primary.main',
                        opacity: isViewOnly ? 0.2 : 1,
                      }}
                      onClick={() => onEditItem(row.id)}
                    >
                      {t('actionEdit')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'grey.600',
                        opacity: isViewOnly ? 0.2 : 1,
                      }}
                      onClick={() =>
                        handleModalDelete(row.id.toString(), interfaceId + '')
                      }
                    >
                      {t('actionDelete')}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default FlowSensorTable
